import { useState } from 'react';
import {
  Modal, Box, Button, Typography,
  CircularProgress,
} from '@mui/material';
import { useStatusMessageDispatch } from '../../hooks/StatusMessageContext';
import { renewToken } from '../../api/tokens/renew-token';
import { isApiError } from '../../utils/errors';
import { TokenExpirationMessageProps } from '../../types';

export const TokenExpirationMessage = ({
  slug,
  publicId,
  currentToken,
}: TokenExpirationMessageProps) => {
  const { setStatusMessage } = useStatusMessageDispatch();
  const [ isResending, setIsResending ] = useState(false);

  const handleResendClick = async () => {
    if (!publicId || !slug || isResending) return;
    setIsResending(true);
    try {
      await renewToken(slug, publicId, currentToken);
      setStatusMessage({
        severity: 'success',
        message: 'Verification email resent',
      });
    } catch (error) {
      if (isApiError(error)) {
        setStatusMessage({
          severity: 'error',
          message: error.message || 'Failed to resend verification email. Please try again later or contact support if the issue persists.',
        });
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Modal
      disableEscapeKeyDown
      aria-describedby='session-expired-description'
      aria-labelledby='session-expired-title'
      hideBackdrop={false}
      open={true}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: {
            xs: 4,
            sm: 6,
          },
          textAlign: 'center',
          overflowY: 'auto',
          maxHeight: '90vh',
        }}
      >
        <Typography
          gutterBottom
          fontWeight={700}
          id='session-expired-title'
          variant='h2'
        >
          Your session has expired
        </Typography>
        <Typography
          id='session-expired-description'
          sx={{ mb: 4 }}
          variant='body1'
        >
          Please click
          {' '}
          <strong>&apos;Resend verification email&apos;</strong>
          {' '}
          to receive a new e-mail to generate new credentials.
        </Typography>
        <Button
          color='primary'
          disabled={isResending}
          size='large'
          sx={{
            px: 5,
            fontWeight: 600,
            borderRadius: '8px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
          variant='contained'
          onClick={handleResendClick}
        >
          {/* Left placeholder to balance spinner width */}
          <Box sx={{
            width: 18,
            height: 18,
            mr: 1,
          }}
          >
            {isResending
              ? (
                <Box sx={{
                  width: 16,
                  height: 16,
                  visibility: 'hidden',
                }}
                />
                )
              : null}
          </Box>

          Resend verification email

          {/* Right spinner */}
          <Box sx={{
            width: 18,
            height: 18,
            ml: 1,
          }}
          >
            {isResending && (
              <CircularProgress
                color='inherit'
                size={16}
              />
            )}
          </Box>
        </Button>
      </Box>
    </Modal>
  );
};

// Add displayName to prevent minification issues in production
TokenExpirationMessage.displayName = '';
